import * as aircoreMedia from '@aircore/aircore-media';
import {ChannelConfiguration, PineridgeChannel} from './pineridge-channel';
import mitt from 'mitt';
import {useCallback, useEffect} from 'react';
import {appLog, log, JSFY} from './util';
import {CameraParams} from './controller';

// This is how we enable react components to "listen" to other code in the app, via events.
const emitter = mitt();

export enum Event {
  // User or action generated events
  USER_BEGIN_ADD_PAK_CHANNEL = 'USER_BEGIN_ADD_PAK_CHANNEL',
  USER_FINISH_ADD_PAK_CHANNEL = 'USER_FINISH_ADD_PAK_CHANNEL',
  USER_EDIT_CHANNEL = 'USER_EDIT_CHANNEL',
  USER_FINISH_EDIT_CHANNEL = 'USER_FINISH_EDIT_CHANNEL',
  USER_REMOVE_CHANNEL = 'USER_REMOVE_CHANNEL',
  USER_JOIN_CHANNEL = 'USER_JOIN_CHANNEL',
  USER_LEAVE_CHANNEL = 'USER_LEAVE_CHANNEL',
  USER_PUBLISH_LOCAL = 'USER_PUBLISH_LOCAL',
  USER_SCREENSHARE_LOCAL = 'USER_SCREENSHARE_LOCAL',
  USER_STOP_PUBLISH = 'USER_STOP_PUBLISH',
  USER_MUTE_REMOTE_AUDIO = 'USER_MUTE_REMOTE_AUDIO',
  USER_UNMUTE_REMOTE_AUDIO = 'USER_UNMUTE_REMOTE_AUDIO',
  USER_MUTE_REMOTE_VIDEO = 'USER_MUTE_REMOTE_VIDEO',
  USER_UNMUTE_REMOTE_VIDEO = 'USER_UNMUTE_REMOTE_VIDEO',
  USER_MUTE_LOCAL_AUDIO = 'USER_MUTE_LOCAL_AUDIO',
  USER_UNMUTE_LOCAL_AUDIO = 'USER_UNMUTE_LOCAL_AUDIO',
  USER_MUTE_LOCAL_VIDEO = 'USER_MUTE_LOCAL_VIDEO',
  USER_UNMUTE_LOCAL_VIDEO = 'USER_UNMUTE_LOCAL_VIDEO',
  USER_START_FRAME_EXTRACTION_LOCAL_VIDEO = 'USER_START_FRAME_EXTRACTION_LOCAL_VIDEO',
  USER_STOP_FRAME_EXTRACTION_LOCAL_VIDEO = 'USER_STOP_FRAME_EXTRACTION_LOCAL_VIDEO',
  USER_APPROVE_PLAYBACK = 'USER_APPROVE_PLAYBACK',
  USER_MOBILE_PASTE = 'USER_MOBILE_PASTE',
  USER_UPDATE_APP_CONFIG = 'USER_UPDATE_PAK_CONFIG',
  USER_SET_USER_AGENT = 'USER_SET_USER_AGENT',
  USER_SET_AUDIO_DEVICE = 'USER_SET_AUDIO_DEVICE',
  USER_SET_VIDEO_DEVICE = 'USER_SET_VIDEO_DEVICE', // Fires when the user selects a new device from the dropdown
  USER_SET_VIDEO_CONFIG = 'USER_SET_VIDEO_CONFIG',
  USER_SET_ADDITIONAL_TAGS = 'USER_SET_ADDITIONAL_TAGS',
  USER_UPDATE_ADDITIONAL_TAGS = 'USER_UPDATE_ADDITIONAL_TAGS',
  USER_CHANGE_VIDEO = 'USER_CHANGE_VIDEO', // Fires when the user clicks the "Change Video" button
  USER_LOG_STREAM_STATS = 'USER_LOG_STREAM_STATS',
  USER_CHANNEL_PENDING_SAT_UPDATE = 'USER_CHANNEL_PENDING_SAT_UPDATE',
  LOG = 'log',
  // Aircore engine or action handler generated response events
  PINERIDGE_ADD_CHANNEL_FROM_PAK = 'PINERIDGE_ADD_CHANNEL_FROM_PAK',
  PINERIDGE_CHANNEL_ADDED = 'PINERIDGE_CHANNEL_ADDED',
  PINERIDGE_EDIT_CHANNEL = 'PINERIDGE_EDIT_CHANNEL',
  PINERIDGE_CHANNEL_REMOVED = 'PINERIDGE_CHANNEL_REMOVED',
  PINERIDGE_DEFAULT_PAK_UPDATED = 'PINERIDGE_DEFAULT_PAK_UPDATED',
  PINERIDGE_PAK_PASTED = 'PINERIDGE_PAK_PASTED',
  PINERIDGE_AUTOSTART_BLOCKED = 'PINERIDGE_AUTOSTART_BLOCKED',
  PINERIDGE_CHANNEL_SAT_UPDATED = 'PINERIDGE_CHANNEL_SAT_UPDATED',
  BIGSUR_CHANNEL_JOINED = 'BIGSUR_CHANNEL_JOINED',
  BIGSUR_CHANNEL_LEFT = 'BIGSUR_CHANNEL_LEFT',
  BIGSUR_CHANNEL_REJOINING = 'BIGSUR_CHANNEL_REJOINING',
  BIGSUR_CHANNEL_JOINING = 'BIGSUR_CHANNEL_JOINING',
  BIGSUR_REMOTE_STREAM_ADDED = 'BIGSUR_REMOTE_STREAM_ADDED',
  BIGSUR_REMOTE_STREAM_REMOVED = 'BIGSUR_REMOTE_STREAM_REMOVED',
  BIGSUR_LOCAL_STREAM_ADDED = 'BIGSUR_LOCAL_STREAM_ADDED',
  BIGSUR_LOCAL_STREAM_REMOVED = 'BIGSUR_LOCAL_STREAM_REMOVED',
  BIGSUR_SCREEN_SHARE_STREAM_ADDED = 'BIGSUR_SCREEN_SHARE_STREAM_ADDED',
  BIGSUR_SCREEN_SHARE_STREAM_REMOVED = 'BIGSUR_SCREEN_SHARE_STREAM_REMOVED',
  BIGSUR_REMOTE_STREAM_LOCAL_AUDIO_MUTE_CHANGED = 'BIGSUR_REMOTE_STREAM_LOCAL_AUDIO_MUTE_CHANGED',
  BIGSUR_REMOTE_STREAM_LOCAL_VIDEO_MUTE_CHANGED = 'BIGSUR_REMOTE_STREAM_LOCAL_VIDEO_MUTE_CHANGED',
  BIGSUR_REMOTE_STREAM_REMOTE_AUDIO_MUTE_CHANGED = 'BIGSUR_REMOTE_STREAM_REMOTE_AUDIO_MUTE_CHANGED',
  BIGSUR_REMOTE_STREAM_REMOTE_VIDEO_MUTE_CHANGED = 'BIGSUR_REMOTE_STREAM_REMOTE_VIDEO_MUTE_CHANGED',
  BIGSUR_REMOTE_STREAM_VAD_CHANGED = 'BIGSUR_REMOTE_STREAM_VAD_CHANGED',
  BIGSUR_REMOTE_STREAM_CONNECTION_CHANGED = 'BIGSUR_REMOTE_STREAM_CONNECTION_CHANGED',
  BIGSUR_LOCAL_STREAM_AUDIO_MUTE_CHANGED = 'BIGSUR_LOCAL_STREAM_AUDIO_MUTE_CHANGED',
  BIGSUR_LOCAL_STREAM_VIDEO_MUTE_CHANGED = 'BIGSUR_LOCAL_STREAM_VIDEO_MUTE_CHANGED',
  BIGSUR_LOCAL_STREAM_CONNECTION_CHANGED = 'BIGSUR_LOCAL_STREAM_CONNECTION_CHANGED',
  BIGSUR_LOCAL_STREAM_VAD_CHANGED = 'BIGSUR_LOCAL_STREAM_VAD_CHANGED',
  BIGSUR_DEVICE_LIST_UPDATED = 'BIGSUR_DEVICE_LIST_UPDATED',
  BIGSUR_CHANNEL_SAT_NEARING_EXPIRY = 'BIGSUR_CHANNEL_SAT_NEARING_EXPIRY',
}

export function emitEvent(event: Event, payload?: unknown) {
  if (event !== Event.LOG) {
    appLog(`emitEvent: ${event}`);
    log('-->payload: ', payload);
  }
  emitter.emit(event, payload);
}

// Use javascript runtime to make this event get fired asynchronously, primarily
// used for logging to the console. Since the console is a react component, we
// cannot have other react components updating the console while it is rendering.
export function queueEvent(e: Event, payload: unknown, queueTime = 0): void {
  setTimeout(() => {
    emitter.emit(e, payload);
  }, queueTime);
}

export function onEvent(event: Event, callback: (payload?: any) => void) {
  emitter.on(event, payload => {
    appLog(`onEvent: ${event}`);
    callback(payload);
  });
}

export function useEventHandler(e: Event, f: (payload?: any) => void, deps: unknown[] = [], logPayload = false) {
  const handler = useCallback((payload: unknown) => {
    if (e !== Event.LOG) {
      if (logPayload) {
        appLog(`onEvent: ${e} payload: ${JSFY(payload)}`);
      } else {
        appLog(`onEvent: ${e}`);
      }
    }
    f(payload);
  }, deps);
  useEffect(() => {
    emitter.on(e, handler);
    return () => emitter.off(e, handler);
  }, [handler]);
}

export type StreamMuteStateChange = {
  stream: aircoreMedia.RemoteStream;
  state: {
    [key in aircoreMedia.RemoteStreamNotificationKey.Muted]: boolean;
  };
};

export type RemoteStreamMuteStateChange = {
  stream: aircoreMedia.RemoteStream;
  state: aircoreMedia.RemoteStreamMuteStateNotification;
};

export type RemoteStreamVadStateChange = {
  stream: aircoreMedia.RemoteStream;
  state: aircoreMedia.RemoteStreamVoiceActivityNotification;
};

export type LocalStreamVadStateChange = {
  stream: aircoreMedia.LocalStream;
  state: aircoreMedia.LocalStreamVoiceActivityNotification;
};

export type LocalStreamMuteStateChange = {
  stream: aircoreMedia.LocalStream;
  state: aircoreMedia.LocalStreamMuteStateNotification;
};

export type LocalStreamConnectionStateChange = {
  stream: aircoreMedia.LocalStream;
  state: aircoreMedia.LocalStreamConnectionStateNotification;
};

export type UserPublishLocalEvent = {
  channel: PineridgeChannel;
  initialAudioMute: boolean;
  initialVideoMute: boolean;
  publishAudioOnly: boolean;
  audioDevice: MediaDeviceInfo | undefined;
  cameraParams: CameraParams;
  additionalTags: string[];
};

export type UserScreenshareLocalEvent = {
  channel: PineridgeChannel;
  additionalTags: string[];
  publishVideoOnly: boolean;
  screenShareSourceId: string | undefined; // only necessary for desktop environments
};

export type UserFrameExtractionEvent = {
  stream: aircoreMedia.LocalStream;
  frameRate: number;
  frameElementId: string;
  setFrameExtraction: (isExtractingFrames: boolean) => void;
};

export type UserStopFrameExtractionEvent = {
  stream: aircoreMedia.LocalStream;
  frameElementId: string;
  setFrameExtraction: (isExtractingFrames: boolean) => void;
};

export type UserUpdateAdditionalTagsEvent = {
  stream: aircoreMedia.LocalStream;
  additionalTags: string[];
};

export type UserSetAudioDeviceEvent = {
  stream: aircoreMedia.LocalStream;
  device: MediaDeviceInfo;
};

export type UserChangeVideoEvent = {
  stream: aircoreMedia.LocalStream;
  cameraParams: CameraParams;
  isAdd: boolean;
};

export type UserEditChannelConfigEvent = {
  pineridgeChannel: PineridgeChannel;
  channelConfig: ChannelConfiguration;
};

export type RemoteStreamConnectionStateChange = {
  stream: aircoreMedia.RemoteStream;
  state: aircoreMedia.RemoteStreamConnectionStateNotification;
};

export type PineridgeChannelLocalStreamPair = {
  pineridgeChannel: PineridgeChannel;
  stream: aircoreMedia.LocalStream;
};

export type PineridgeChannelRemoteStreamPair = {
  pineridgeChannel: PineridgeChannel;
  stream: aircoreMedia.RemoteStream;
};
